/* ──────────────────────────────────────────────────────────────────────────────
   Faz 5 — Depozito Mutabakatı (backoffice)  ·  /deposit-reconciliation

   ══ BU EKRANIN VARLIK SEBEBİ ══════════════════════════════════════════════════
   Depozito borcu İKİ TARAFTA tutulur — bizde ve SAP'de — ve BİRBİRİNİ TUTMAYABİLİR
   (spec §1). Ekran bu farkı görünür kılar; farkın kendisi ekranın konusudur.

   ÜÇ BLOK (spec §8):
     ① Mutabakatsız plasiyerler — EN ÜSTTE, KIRMIZI. Bizim bakiyemiz ile SAP'nin
        bildiği (ExternalBalance) farkı. Aksiyon: "Sayım İste" · "Düzeltme Kaydı Gir"
        (GEREKÇE ZORUNLU — gerekçesiz düzeltme, mutabakatsızlığın sebebini (taşıma kaybı
        mı, suistimal mi, SAP hatası mı) sonsuza dek gizler).
     ② Yüksek depozito borcu — eşiği aşan plasiyerler. SEPET, PARA DEMEKTİR; sahada
        birikmesi istenmez.
     ③ İrsaliye geçmişi — kırık oranı burada da izlenir.

   ══ ① ve ② KARAR BLOĞUDUR, LİSTE DEĞİL — c360'a ÇEVRİLMEZ ══════════════════════
   İkisi de TOPLULAŞTIRILMIŞ karar tablolarıdır (① fark hesabı, ② plasiyer bazında
   toplam borç): sayfalanacak bir kayıt kümesi değil, "kime bugün müdahale edilecek"
   sorusunun cevabıdır. Sunucu-taraflı sayfalama onları anlamsız kılardı — 2. sayfaya
   düşen mutabakatsızlık, görülmemiş mutabakatsızlıktır.
   ③ ise gerçek bir BELGE LİSTESİDİR → c360 (useC360List) ile sunucuda sayfalanır.

   ⚠ ExternalBalance NULL = SAP henüz veri göndermedi: bu bir FARK değil BOŞLUKTUR,
   mutabakatsız SAYILMAZ. Aksi halde entegrasyon açılmadan ekran herkesi kırmızı gösterirdi.

   ⚠ TUTAR YOKTUR — hiçbir kolonda para gösterilmez (iş kuralı 4). "Sepet paradır" bir
   uyarıdır, bir para kolonu değil.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, RelationPicker } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, buildSelectFields, unwrapLookupItems,
} from './c360';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { DATE_PRESETS, fmtDate, fmtQty } from './returnShared';
import {
  RETURNS_API, BALANCES_API, fetchReconciliation, isMismatch, daysSince, fmtPct,
  statusLabel, statusTone, damageRate, documentDateOf,
  STATUS_SELECT_OPTIONS, highDamageFilter, recentDaysFilter,
  productLineFilter, supportsLineFilter,
  DAMAGE_RATE_THRESHOLD, HIGH_DEBT_THRESHOLD, NO_RETURN_DAYS_THRESHOLD,
} from './depositShared';

const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const rc = {
  blk: { borderTop: border, padding: '14px 16px' },
  blkDanger: { background: 'var(--danger-soft, rgba(226,68,92,0.07))' },
  blkTitle: {
    margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
    color: muted, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  blkTitleDanger: { color: 'var(--danger)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  td: {
    padding: '9px', whiteSpace: 'nowrap', color: 'var(--text-primary, #111827)',
    borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))',
  },
  num: { textAlign: 'right' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
  danger: { color: 'var(--danger)', fontWeight: 800 },
  warn: { color: 'var(--warning, #b45309)', fontWeight: 700 },
  ok: { color: 'var(--success, #16a34a)', fontWeight: 700 },
  muted: { color: muted },
  empty: { padding: '14px 4px', fontSize: 13, color: muted },
  note: { margin: '9px 0 0', fontSize: 11, color: muted, lineHeight: 1.6 },
  smallBtn: { ...s.btnSecondary, padding: '5px 9px', fontSize: 11, minWidth: 0, height: 'auto' },
  smallBtnPrimary: { ...s.btnPrimary, padding: '5px 9px', fontSize: 11, minWidth: 0, height: 'auto' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  filterInput: { ...s.input, height: 34, fontSize: 12, borderRadius: 6, width: 130 },
  filterNum: { ...s.input, height: 34, fontSize: 12, borderRadius: 6, width: 90 },
  filterSelect: { ...s.select, height: 34, fontSize: 12, borderRadius: 6, width: 160 },
  lookupBox: { width: 200 },
  searchInput: { ...s.input, height: 30, fontSize: 12, borderRadius: 6, width: 190 },
  chip: {
    padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    border: '1px solid var(--border-color, rgba(15,23,42,0.14))', background: 'var(--bg-card, #fff)',
    color: muted, userSelect: 'none',
  },
  chipOn: { background: 'var(--accent)', color: '#fff', borderColor: 'transparent' },
  chipDanger: { background: 'var(--danger)', color: '#fff', borderColor: 'transparent' },
};

/**
 * Düzeltme kaydı — GEREKÇE ZORUNLU. Boş gerekçeyle gönderilemez (buton kilitli); backend de
 * reddeder. Düzeltme kalıcıdır: gece yeniden-hesap job'ı onu KORUR, ezmez.
 */
function AdjustModal({ row, busy, onClose, onSubmit }) {
  // Farkı kapatmak için gereken işaretli delta: bakiyemiz SAP'den 6 fazlaysa −6 eklenmelidir.
  const suggested = row.difference === null ? '' : String(-row.difference);
  const [quantity, setQuantity] = useState(suggested);
  const [reason, setReason] = useState('');
  const qty = Number(quantity);
  const validQty = quantity !== '' && !Number.isNaN(qty) && qty !== 0;
  const canSubmit = validQty && reason.trim().length > 0 && !busy;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>Düzeltme Kaydı Gir — {row.representativeName} · {row.productName}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: muted, lineHeight: 1.6 }}>
            Bizde <b>{fmtQty(row.ours)}</b> · SAP'de <b>{row.externalBalance === null ? '—' : fmtQty(row.externalBalance)}</b>
            {row.difference !== null && <> · Fark <b style={rc.danger}>{row.difference > 0 ? '+' : ''}{fmtQty(row.difference)}</b></>}
            . Girilen miktar İŞARETLİ bir delta'dır (± ) ve bakiyeye eklenir.
          </p>
          <div style={s.formRow}>
            <div style={s.formGroup}>
              <label style={s.label}>Düzeltme Miktarı (±) *</label>
              <input
                type="number"
                style={{ ...s.input, width: '100%' }}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Örn. -6"
              />
              {!validQty && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
                  Miktar sıfır olamaz.
                </div>
              )}
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Gerekçe * (zorunlu — denetim izi)</label>
            <textarea
              style={{ ...s.input, minHeight: 78, resize: 'vertical', width: '100%' }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Fark neden düzeltiliyor? Taşımada kayıp mı, SAP hatası mı, sayım farkı mı?"
            />
            {!reason.trim() && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
                Gerekçe ZORUNLUDUR. Bakiye para demektir; gerekçesiz düzeltme denetim izini yok eder.
              </div>
            )}
          </div>
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
          <button
            style={{ ...s.btnPrimary, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            disabled={!canSubmit}
            onClick={() => onSubmit({ quantity: qty, reason: reason.trim() })}
          >
            {busy ? 'İşleniyor…' : 'Düzeltmeyi Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DepositReconciliationPage() {
  const navigate = useNavigate();
  const { canEdit } = usePageActions();
  usePageMeta('Depozito Mutabakatı', 'Bizdeki bakiye ile SAP bakiyesi arasındaki fark, yüksek borç ve irsaliye geçmişi.');

  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [adjustRow, setAdjustRow] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const [representatives, setRepresentatives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Filtreler (spec §8 blok 3: tarih · plasiyer · araç · ürün)
  const [datePreset, setDatePreset] = useState('30');
  const [repId, setRepId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [productText, setProductText] = useState('');
  const [mismatchOnly, setMismatchOnly] = useState(true);
  const [onlyHighDamage, setOnlyHighDamage] = useState(false);
  const [debtThreshold, setDebtThreshold] = useState(HIGH_DEBT_THRESHOLD);
  const [daysThreshold, setDaysThreshold] = useState(NO_RETURN_DAYS_THRESHOLD);

  // `Lines/any(...)` bu uçta çalışıyor mu (③'ün ürün filtresi buna bağlı). null = henüz bilinmiyor.
  const [lineFilterOk, setLineFilterOk] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, w]) => {
      setRepresentatives(unwrapLookupItems(r));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => { /* ad çözümlenemezse hücre '-' gösterir; ekran çalışmaya devam eder */ });

    supportsLineFilter(headers).then(setLineFilterOk);
  }, []);

  const repName = useCallback(
    (id) => representatives.find((x) => x.id === id)?.name || '-',
    [representatives],
  );
  const vehicleName = useCallback(
    (id) => {
      const w = warehouses.find((x) => x.id === id);
      return w ? (w.name || w.textId || '-') : '-';
    },
    [warehouses],
  );

  const productQuery = productText.trim().toLocaleLowerCase('tr');

  // ── ①② BAKİYELER (karar blokları — tek atımda iner, ekranda ayrıştırılır) ────
  const loadBalances = useCallback(async () => {
    setLoading(true);
    try {
      setBalances(await fetchReconciliation(authHeaders()));
    } catch (e) {
      showToast('error', e.message || 'Mutabakat verisi alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  // ── ③ İRSALİYE GEÇMİŞİ (c360 — sunucu taraflı sayfalama/sıralama/huni) ───────
  const columnFilterMeta = useMemo(() => ({
    status: { type: 'select', odataField: 'Status', options: STATUS_SELECT_OPTIONS },
  }), []);
  const searchFields = useMemo(() => ['DocumentNo'], []);

  // Toolbar filtreleri BLOKLARIN TÜMÜNE uygulanır → ③ için OData $filter'a çevrilirler.
  // Ürün filtresi kalem verisi ister: uç `Lines/any(...)` desteklemiyorsa UYGULANMAZ ve satır
  // ELENMEZ — bilinmeyeni yokmuş gibi saymak listeyi sessizce boşaltırdı (eski ekranın kuralı).
  const baseFilter = useMemo(() => {
    const parts = [];
    const dateClause = recentDaysFilter(datePreset);
    if (dateClause) parts.push(dateClause);
    if (repId) parts.push(`RepresentativeId eq ${repId}`);
    if (warehouseId) parts.push(`WarehouseId eq ${warehouseId}`);
    if (onlyHighDamage) parts.push(highDamageFilter());
    if (productQuery && lineFilterOk) {
      const clause = productLineFilter(productQuery);
      if (clause) parts.push(clause);
    }
    return parts.join(' and ');
  }, [datePreset, repId, warehouseId, onlyHighDamage, productQuery, lineFilterOk]);

  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta,
    columnVisibility,
    always: [
      'Id', 'DocumentNo', 'RepresentativeId', 'WarehouseId',
      'TotalQuantity', 'TotalDamaged', 'Status', 'SentAt', 'CreatedDate',
    ],
  }), [columnVisibility, columnFilterMeta]);

  const list = useC360List({
    apiBase: RETURNS_API,
    baseFilter,
    columnFilterMeta,
    searchFields,
    defaultSortField: 'CreatedDate',
    defaultSortDir: 'desc',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows: historyRows, totalCount: historyCount, loading: historyLoading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData: fetchHistory,
  } = list;

  const refreshAll = useCallback(() => { loadBalances(); fetchHistory(); }, [loadBalances, fetchHistory]);

  // ── ① MUTABAKATSIZ ────────────────────────────────────────────────────────────
  const mismatchRows = useMemo(() => balances.filter((b) => {
    if (repId && b.representativeId !== repId) return false;
    if (productQuery && !String(b.productName || '').toLocaleLowerCase('tr').includes(productQuery)) return false;
    if (mismatchOnly && !isMismatch(b)) return false;
    return true;
  }).sort((a, b) => Math.abs(b.difference ?? 0) - Math.abs(a.difference ?? 0)), [balances, repId, productQuery, mismatchOnly]);

  const mismatchCount = useMemo(() => balances.filter(isMismatch).length, [balances]);

  // ── ② YÜKSEK BORÇ (plasiyer bazında toplulaştırma) ────────────────────────────
  //    Bakiye PLASİYER bazındadır (borç kişiye aittir); stok araç bazında. İki eksen ayrıdır.
  const debtRows = useMemo(() => {
    const byRep = new Map();
    balances.forEach((b) => {
      if (repId && b.representativeId !== repId) return;
      const key = b.representativeId;
      const acc = byRep.get(key) || {
        representativeId: key, representativeName: b.representativeName,
        crate: 0, bottle: 0, other: 0, total: 0, atCustomer: 0, lastReturnAt: null,
      };
      const bal = Number(b.ours || 0);
      if (b.cls === 'CRATE') acc.crate += bal;
      else if (b.cls === 'BOTTLE') acc.bottle += bal;
      else acc.other += bal;
      acc.total += bal;
      acc.atCustomer += Number(b.atCustomer || 0);
      const t = b.lastReturnAt ? new Date(b.lastReturnAt).getTime() : null;
      if (t && (!acc.lastReturnAt || t > acc.lastReturnAt)) acc.lastReturnAt = t;
      byRep.set(key, acc);
    });
    return [...byRep.values()]
      .map((x) => ({ ...x, days: daysSince(x.lastReturnAt) }))
      .filter((x) => x.total >= Number(debtThreshold || 0)
        || (x.days !== null && x.days >= Number(daysThreshold || 0))
        || (x.days === null && x.total > 0))
      .sort((a, b) => b.total - a.total);
  }, [balances, repId, debtThreshold, daysThreshold]);

  // ── AKSİYONLAR ───────────────────────────────────────────────────────────────
  const requestCount = async (row) => {
    setBusy(true);
    try {
      const res = await fetch(`${BALANCES_API}/${row.id}/request-count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reason: `Mutabakatsızlık: bizde ${row.ours}, SAP'de ${row.externalBalance ?? '—'}` }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Sayım talebi oluşturulamadı');
      showToast('success', 'Sayım talebi plasiyere gönderildi.');
      refreshAll();
    } catch (e) {
      showToast('error', e.message || 'Sayım talebi oluşturulamadı');
    } finally {
      setBusy(false);
    }
  };

  const submitAdjust = async ({ quantity, reason }) => {
    setBusy(true);
    try {
      const res = await fetch(`${BALANCES_API}/${adjustRow.id}/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ quantity, reason }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Düzeltme kaydedilemedi');
      showToast('success', 'Düzeltme kaydedildi — gerekçesiyle birlikte iz bırakıldı.');
      setAdjustRow(null);
      refreshAll();
    } catch (e) {
      showToast('error', e.message || 'Düzeltme kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const handleExport = () => {
    if (mismatchRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    const cols = [
      { headerName: 'Plasiyer', valueGetter: ({ row }) => row.representativeName || '' },
      { headerName: 'Ürün', valueGetter: ({ row }) => row.productName || '' },
      { headerName: 'Bizde', valueGetter: ({ row }) => Number(row.ours || 0) },
      { headerName: "SAP'de", valueGetter: ({ row }) => (row.externalBalance === null ? '' : Number(row.externalBalance)) },
      { headerName: 'Fark', valueGetter: ({ row }) => (row.difference === null ? '' : Number(row.difference)) },
      { headerName: 'Müşteride', valueGetter: ({ row }) => Number(row.atCustomer || 0) },
      { headerName: 'Araçta', valueGetter: ({ row }) => Number(row.onVehicle || 0) },
      { headerName: 'Son Mutabakat', valueGetter: ({ row }) => fmtDate(row.lastReconciledAt) },
    ];
    exportToExcel(mismatchRows, cols, `depozito-mutabakat_${new Date().toISOString().slice(0, 10)}`);
    showToast('success', `${mismatchRows.length} kayıt dışa aktarıldı`);
  };

  // ── ③ Kolonlar. TUTAR KOLONU BİLİNÇLİ OLARAK YOK.
  const columnHelper = createColumnHelper();
  const historyColumns = useMemo(() => [
    columnHelper.accessor('documentNo', {
      header: 'İrsaliye No',
      cell: (info) => <span style={rc.mono}>{info.getValue() || '(taslak)'}</span>,
    }),
    columnHelper.accessor('createdDate', {
      header: 'Tarih',
      cell: ({ row }) => fmtDate(documentDateOf(row.original)),
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer', enableSorting: false,
      cell: ({ row }) => repName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'vehicle', header: 'Araç', enableSorting: false,
      cell: ({ row }) => <span style={rc.mono}>{vehicleName(row.original.warehouseId)}</span>,
    }),
    columnHelper.accessor('totalQuantity', {
      header: 'Adet',
      cell: (info) => <span style={{ ...rc.num, fontWeight: 700 }}>{fmtQty(info.getValue())}</span>,
    }),
    columnHelper.accessor('totalDamaged', {
      header: 'Kırık',
      cell: (info) => <span style={rc.num}>{fmtQty(info.getValue())}</span>,
    }),
    // TÜRETİLMİŞ (DB'de kolon yok) → sıralanamaz/filtrelenemez; "⚠ Yüksek kırık oranı" ile daraltılır.
    columnHelper.display({
      id: 'damageRate', header: 'Kırık %', enableSorting: false,
      cell: ({ row }) => {
        const rate = damageRate(row.original.totalQuantity, row.original.totalDamaged);
        const high = rate !== null && rate >= DAMAGE_RATE_THRESHOLD;
        return <span style={{ ...rc.num, ...(high ? rc.danger : rc.ok) }}>{fmtPct(rate)}</span>;
      },
    }),
    columnHelper.accessor('status', {
      header: 'Durum',
      meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.display({
      id: 'actions', header: '', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
                  title="Görüntüle" aria-label="Görüntüle"
                  onClick={(e) => { e.stopPropagation(); navigate(`/deposit-returns/${row.original.id}`); }}>
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [repName, vehicleName, navigate]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('CreatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const historyPages = Math.max(1, Math.ceil(historyCount / pageSize));
  const historyTable = useReactTable({
    data: historyRows,
    columns: historyColumns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: historyPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const toolbarNode = (
    <div style={rc.filters}>
      <select style={rc.filterSelect} value={datePreset} onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(1); }}>
        {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>Tarih: {d.label}</option>)}
      </select>
      <div style={rc.lookupBox}>
        <RelationPicker
          config={RELATION_CONFIGS.representative}
          value={repId}
          selectedLabel={repId ? repName(repId) : ''}
          onChange={(v) => { setRepId(v || ''); setCurrentPage(1); }}
          placeholder="Plasiyer: Tümü"
        />
      </div>
      <div style={rc.lookupBox}>
        <RelationPicker
          config={RELATION_CONFIGS.warehouse}
          value={warehouseId}
          selectedLabel={warehouseId ? vehicleName(warehouseId) : ''}
          onChange={(v) => { setWarehouseId(v || ''); setCurrentPage(1); }}
          placeholder="Araç: Tümü"
        />
      </div>
      <input style={rc.filterInput} placeholder="Ürün" value={productText}
             title={lineFilterOk === false
               ? 'Ürün filtresi bakiye bloklarına uygulanır; irsaliye geçmişinde kalem verisi bu uçta sorgulanamıyor.'
               : 'Ürün adına göre daralt (bakiye blokları + irsaliye kalemleri).'}
             onChange={(e) => { setProductText(e.target.value); setCurrentPage(1); }} />
      <span style={{ ...rc.chip, ...(mismatchOnly ? rc.chipOn : null) }} role="button"
            title="SAP verisi olmayan satırlar mutabakatsız SAYILMAZ — o bir fark değil, boşluktur."
            onClick={() => setMismatchOnly((v) => !v)}>
        Sadece mutabakatsız
      </span>
      <span style={{ ...rc.chip, ...(onlyHighDamage ? rc.chipDanger : null) }} role="button"
            title={`İrsaliye geçmişinde kırık oranı %${DAMAGE_RATE_THRESHOLD} ve üzeri belgeler.`}
            onClick={() => { setOnlyHighDamage((v) => !v); setCurrentPage(1); }}>
        ⚠ Yüksek kırık oranı
      </span>
      <label style={{ fontSize: 11, color: muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        Borç eşiği
        <input type="number" style={rc.filterNum} value={debtThreshold}
               onChange={(e) => setDebtThreshold(e.target.value)} title="Yüksek borç eşiği (adet)" />
      </label>
      <label style={{ fontSize: 11, color: muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        Gün eşiği
        <input type="number" style={rc.filterNum} value={daysThreshold}
               onChange={(e) => setDaysThreshold(e.target.value)} title="Kaç gündür iade yok eşiği" />
      </label>
      <button style={s.btnSecondary} onClick={refreshAll} disabled={loading}>
        {loading ? 'Yükleniyor…' : 'Yenile'}
      </button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </div>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone={mismatchCount ? 'bad' : 'ok'}>{mismatchCount} mutabakatsız</StatusBadge>
      <StatusBadge tone={debtRows.length ? 'wait' : 'ok'}>{debtRows.length} yüksek borç</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Depozito Mutabakatı"
        subtitle="Bakiye iki tarafta tutulur ve birbirini tutmayabilir. Sepet, para demektir; sahada birikmesi istenmez."
        meta={metaNode}
        toolbar={toolbarNode}
      >
        {/* ① MUTABAKATSIZ PLASİYERLER — EN ÜSTTE, KIRMIZI */}
        <div style={{ ...rc.blk, ...rc.blkDanger, borderTop: 'none' }}>
          <p style={{ ...rc.blkTitle, ...rc.blkTitleDanger }}>
            ⚖ Mutabakatsız plasiyerler — bizim bakiyemiz ile SAP farklı
            <StatusBadge tone={mismatchCount ? 'bad' : 'ok'}>{mismatchRows.length} satır</StatusBadge>
          </p>
          {loading ? (
            <div style={rc.empty}>Yükleniyor…</div>
          ) : mismatchRows.length === 0 ? (
            <div style={rc.empty}>
              {mismatchOnly
                ? 'Mutabakatsız satır yok. (SAP verisi gelmemiş satırlar burada SAYILMAZ — fark değil, boşluktur.)'
                : 'Bakiye satırı yok.'}
            </div>
          ) : (
            <table style={rc.table}>
              <thead>
                <tr>
                  <th style={rc.th}>Plasiyer</th>
                  <th style={rc.th}>Ürün</th>
                  <th style={{ ...rc.th, ...rc.num }}>Bizde</th>
                  <th style={{ ...rc.th, ...rc.num }}>SAP&apos;de</th>
                  <th style={{ ...rc.th, ...rc.num }}>Fark</th>
                  <th style={rc.th}>Son mutabakat</th>
                  <th style={{ ...rc.th, textAlign: 'right' }}>Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {mismatchRows.map((b) => {
                  const diff = b.difference;
                  const requested = b.countRequestedAt ? daysSince(b.countRequestedAt) : null;
                  return (
                    <tr key={b.id}>
                      <td style={rc.td}>{b.representativeName || repName(b.representativeId)}</td>
                      <td style={rc.td}>{b.productName || '-'}</td>
                      <td style={{ ...rc.td, ...rc.num, fontWeight: 700 }}>{fmtQty(b.ours)}</td>
                      <td style={{ ...rc.td, ...rc.num }}>
                        {b.externalBalance === null
                          ? <span style={rc.muted} title="SAP henüz veri göndermedi — bu bir fark değil, boşluktur.">—</span>
                          : fmtQty(b.externalBalance)}
                      </td>
                      <td style={{ ...rc.td, ...rc.num, ...(diff ? rc.danger : rc.muted) }}>
                        {diff === null ? '—' : `${diff > 0 ? '+' : ''}${fmtQty(diff)}`}
                      </td>
                      <td style={{ ...rc.td, ...rc.muted }}>
                        {b.lastReconciledAt ? fmtDate(b.lastReconciledAt) : '—'}
                      </td>
                      <td style={{ ...rc.td, textAlign: 'right' }}>
                        {canEdit ? (
                          <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button type="button" style={rc.smallBtnPrimary} disabled={busy}
                                    title={requested !== null ? `Son talep ${requested} gün önce gönderildi.` : 'Plasiyerden depozito sayımı iste.'}
                                    onClick={() => requestCount(b)}>
                              {requested !== null ? `Sayım İste (${requested}g)` : 'Sayım İste'}
                            </button>
                            <button type="button" style={rc.smallBtn} disabled={busy}
                                    title="Manuel bakiye düzeltmesi — gerekçe zorunludur."
                                    onClick={() => setAdjustRow(b)}>
                              Düzeltme Gir
                            </button>
                          </span>
                        ) : <span style={rc.muted}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p style={rc.note}>
            Düzeltme kaydı GEREKÇESİZ girilemez: gerekçe, farkın sebebini (taşıma kaybı · sayım hatası ·
            SAP hatası · suistimal) sonraki denetime taşıyan tek izdir.
          </p>
        </div>

        {/* ② YÜKSEK DEPOZİTO BORCU */}
        <div style={rc.blk}>
          <p style={rc.blkTitle}>
            ⚠ Yüksek depozito borcu — sepet, paradır
            <StatusBadge tone={debtRows.length ? 'wait' : 'ok'}>{debtRows.length} plasiyer</StatusBadge>
          </p>
          {loading ? (
            <div style={rc.empty}>Yükleniyor…</div>
          ) : debtRows.length === 0 ? (
            <div style={rc.empty}>Eşiği aşan plasiyer yok.</div>
          ) : (
            <table style={rc.table}>
              <thead>
                <tr>
                  <th style={rc.th}>Plasiyer</th>
                  <th style={{ ...rc.th, ...rc.num }}>Sepet</th>
                  <th style={{ ...rc.th, ...rc.num }}>Şişe</th>
                  <th style={{ ...rc.th, ...rc.num }}>Diğer</th>
                  <th style={{ ...rc.th, ...rc.num }}>Toplam borç</th>
                  <th style={rc.th}>Kaç gündür iade yok</th>
                  <th style={{ ...rc.th, ...rc.num }}>Müşteride kalan</th>
                </tr>
              </thead>
              <tbody>
                {debtRows.map((r) => {
                  const late = r.days === null || r.days >= Number(daysThreshold || 0);
                  return (
                    <tr key={r.representativeId}>
                      <td style={rc.td}>{r.representativeName || repName(r.representativeId)}</td>
                      <td style={{ ...rc.td, ...rc.num, fontWeight: 700 }}>{fmtQty(r.crate)}</td>
                      <td style={{ ...rc.td, ...rc.num }}>{fmtQty(r.bottle)}</td>
                      <td style={{ ...rc.td, ...rc.num, ...rc.muted }}>{fmtQty(r.other)}</td>
                      <td style={{
                        ...rc.td, ...rc.num,
                        ...(r.total >= Number(debtThreshold || 0) ? rc.danger : { fontWeight: 700 }),
                      }}>
                        {fmtQty(r.total)}
                      </td>
                      <td style={{ ...rc.td, ...(late ? rc.danger : rc.muted) }}>
                        {r.days === null ? 'Hiç iade yok' : `${r.days} gün`}
                      </td>
                      <td style={{ ...rc.td, ...rc.num }}>{fmtQty(r.atCustomer)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p style={rc.note}>
            &quot;Müşteride kalan&quot; sayılmaz, TÜRETİLİR: <b>Müşteride = Bakiye − araçtaki depozito stoğu</b>.
            Borç plasiyere aittir (araç değiştirince kaybolmaz); stok ise araç bazındadır.
          </p>
        </div>

        {/* ③ İRSALİYE GEÇMİŞİ — c360 listesi (sunucu taraflı sayfalama/sıralama/huni) */}
        <div style={rc.blk}>
          <p style={rc.blkTitle}>
            ⟳ İrsaliye geçmişi · kırık oranı izlenir
            <StatusBadge tone="neutral">{historyCount} irsaliye</StatusBadge>
            <input
              style={rc.searchInput}
              type="text"
              placeholder="İrsaliye no ile ara..."
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
            />
            {filterText && (
              <button type="button" style={rc.smallBtn} onClick={() => { setFilterInput(''); applyFilterText(''); }}>
                Aramayı temizle
              </button>
            )}
            <ColumnChooser table={historyTable} />
            <button type="button" style={rc.smallBtn} onClick={() => navigate('/deposit-returns')}>
              Tüm irsaliyeler
            </button>
          </p>

          <C360Table
            table={historyTable}
            columnFilterMeta={columnFilterMeta}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={historyLoading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={(row) => navigate(`/deposit-returns/${row.id}`)}
            emptyText="Seçilen filtrede irsaliye yok."
          />

          <Pagination
            currentPage={currentPage}
            totalCount={historyCount}
            pageSize={pageSize}
            rowCount={historyRows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />

          <p style={rc.note}>
            Sürekli yüksek kırık bildiren plasiyer, ya bir taşıma sorununa ya da SUİSTİMALE işaret eder.
            Kırık adet iade edilenin İÇİNDEDİR — bakiyeden düşer, belgeyi ikiye bölmez.
            {lineFilterOk === false && ' Ürün filtresi bu blokta uygulanmaz (kalem verisi bu uçta sorgulanamıyor) — satırlar elenmez.'}
          </p>
        </div>
      </ListPageShell>

      {adjustRow && (
        <AdjustModal
          row={adjustRow}
          busy={busy}
          onClose={() => setAdjustRow(null)}
          onSubmit={submitAdjust}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default DepositReconciliationPage;
